import {
  createContext,
  ReactNode,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { AccountInfo, PublicClientApplication } from "@azure/msal-browser";
import { createMsalInstance } from "../msal/authFactory";
import { AuthState, JWTContextType } from "../@types/authentication";
import { useNavigate } from "react-router-dom";
import baseService from "../services/base.service";
import { enqueueSnackbar } from "notistack";

enum Types {
  Initial = "INITIALIZE",
  Login = "LOGIN",
  Logout = "LOGOUT",
  Register = "REGISTER",
}

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  isLoading: false,
};

const JWTReducer = (state: AuthState, action: any) => {
  switch (action.type) {
    case Types.Initial:
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
        companyInfo: action.payload.companyInfo,
        isLoading: action.payload.isLoading,
      };
    case Types.Login:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        companyInfo: action.payload.companyInfo,
        isLoading: action.payload.isLoading,
      };
    case Types.Logout:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        companyInfo: null,
        isLoading: false,
      };
    default:
      return state;
  }
};

export const AzureEntraContext = createContext<JWTContextType | null>(null);

let _getToken: (() => Promise<string | null>) | null = null;

export const getMsalToken = async () => {
  if (_getToken) {
    return await _getToken();
  }
  return null;
};

function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(JWTReducer, initialState);
  const msalRef = useRef<PublicClientApplication | null>(null);
  const loginReqRef = useRef<any>(null);
  const initializedRef = useRef(false);

  const [msalInstance, setMsalInstance] =
    useState<PublicClientApplication | null>(null);

  useEffect(() => {
    const cfg = sessionStorage.getItem("postLoginAuthConfig");
    const persistedAuth = localStorage.getItem("activeAuthConfig");
    if (!initializedRef.current && cfg) {
      // show loading while resuming redirect flow
      dispatch({
        type: Types.Initial,
        payload: {
          isAuthenticated: false,
          user: null,
          companyInfo: null,
          isLoading: true,
        },
      });
      const parsed = JSON.parse(cfg);
      (async () => {
        await initMsal(parsed);
      })();
    } else if (!initializedRef.current && persistedAuth) {
      // no pending redirect but we have a previously used auth config → restore session
      dispatch({
        type: Types.Initial,
        payload: {
          isAuthenticated: false,
          user: null,
          companyInfo: null,
          isLoading: true,
        },
      });
      const parsed = JSON.parse(persistedAuth);
      (async () => {
        await initMsal(parsed);
      })();
    } else if (!initializedRef.current) {
      dispatch({
        type: Types.Initial,
        payload: {
          isAuthenticated: false,
          user: null,
          companyInfo: null,
          isLoading: false,
        },
      });
      initializedRef.current = true;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handshakeWithBackend = async (accessToken: any): Promise<boolean> => {
    try {
      const res = await baseService
        .post(
          `/user/entra-login?access_token=${accessToken}`,
          {
            headers: { "Content-Type": "application/json" },
          }
        )
        .then((r) => r.data);

      const loginResponse = res?.data;

      if (loginResponse?.isSuccess === true) {
        return loginResponse;
      } else {
        return false;
      }
    } catch (e) {
      console.warn("Handshake failed:", e);
      return false;
    }
  };

  const initMsal = async (authConfig: any) => {
    try {
      // start loading while initializing msal
      dispatch({
        type: Types.Initial,
        payload: {
          isAuthenticated: state.isAuthenticated,
          user: state.user,
          companyInfo: state.companyInfo,
          isLoading: true,
        },
      });
      if (!msalRef.current) {
        const { msalInstance: inst, loginRequest } =
          createMsalInstance(authConfig);
  
        msalRef.current = inst;
        loginReqRef.current = loginRequest;
  
        setMsalInstance(inst);
  
        await inst.initialize();
        sessionStorage.removeItem("postLoginAuthConfig");
  
        const response = await inst.handleRedirectPromise();
        if (response?.account) {
          const loginToService = await handshakeWithBackend(
            response?.accessToken
          );
          if (!loginToService) {
            dispatch({
              type: Types.Initial,
              payload: { isAuthenticated: false, user: null, isLoading: false },
            });
            enqueueSnackbar(
              "Giriş başarısız oldu. Teknik ekip ile iletişime geçiniz.",
              { variant: "error" }
            );
            await logout();
            return;
          } else {
            inst.setActiveAccount(response.account);
            setUserInfo(response.account, loginToService);
            const from =
              sessionStorage.getItem("postLoginRedirect") || "/dashboard/app";
            sessionStorage.removeItem("postLoginRedirect");
            navigate(from, { replace: true });
            // login done, stop loading
            dispatch({
              type: Types.Initial,
              payload: {
                isAuthenticated: true,
                user: response.account,
                companyInfo: loginToService,
                isLoading: false,
              },
            });
          }
        } else {
          const acc = inst.getAllAccounts()[0];
  
          if (acc) {
            inst.setActiveAccount(acc);
            const loginToService = await handshakeWithBackend(
              await getMsalToken()
            );
            if (loginToService) {
              setUserInfo(acc, loginToService);
              dispatch({
                type: Types.Initial,
                payload: {
                  isAuthenticated: true,
                  user: acc,
                  companyInfo: loginToService,
                  isLoading: false,
                },
              });
            } else {
              dispatch({
                type: Types.Initial,
                payload: {
                  isAuthenticated: false,
                  user: null,
                  companyInfo: null,
                  isLoading: false,
                },
              });
              enqueueSnackbar(
                "Giriş başarısız oldu. Teknik ekip ile iletişime geçiniz.",
                { variant: "error" }
              );
              await logout();
            }
          } else {
            dispatch({
              type: Types.Initial,
              payload: {
                isAuthenticated: false,
                user: null,
                companyInfo: null,
                isLoading: false,
              },
            });
          }
        }
      }
    } catch (error) {
      console.log("initMsal error", error);
      await logout();
    }
 
  };

  const login = async (
    authConfig: any,
    redirectTo: string = "/dashboard/app"
  ) => {
    // Eğer zaten giriş yapılmışsa ve farklı bir şirket kimliği ile giriş yapılmaya çalışılıyorsa
    if (state.isAuthenticated && state.companyInfo?.companyId !== authConfig?.companyId) {
      console.log("Farklı şirket ile giriş yapılmaya çalışılıyor, önceki kullanıcı logout ediliyor");
      await logout();
    }

    // indicate loading while starting login
    dispatch({
      type: Types.Initial,
      payload: {
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        companyInfo: state.companyInfo,
        isLoading: true,
      },
    });

    // persist config for future refresh restores
    try {
      localStorage.setItem("activeAuthConfig", JSON.stringify(authConfig));
    } catch {}

    await initMsal(authConfig);

    const inst = msalRef.current;
    const loginReq = loginReqRef.current;
    if (!inst || !loginReq) return;

    const acc = inst.getActiveAccount() ?? inst.getAllAccounts()[0];
    if (acc) {
      setUserInfo(acc, state.companyInfo);
      navigate(redirectTo, { replace: true });
      dispatch({
        type: Types.Initial,
        payload: {
          isAuthenticated: true,
          user: acc,
          companyInfo: state.companyInfo ?? null,
          isLoading: false,
        },
      });
      return;
    }

    sessionStorage.setItem("postLoginRedirect", redirectTo);
    sessionStorage.setItem("postLoginAuthConfig", JSON.stringify(authConfig));
    sessionStorage.setItem("companyId", authConfig?.companyId);

    await inst.loginRedirect(loginReq);
  };

  const clearAuthCache = async () => {
    const inst = msalRef.current;
    if (inst) {
      try {
        inst.setActiveAccount(null);
        // Tüm hesapları temizle
        const accounts = inst.getAllAccounts();
        for (let i = 0; i < accounts.length; i++) {
          try {
            await inst.logoutRedirect();
          } catch (err) {
            console.warn("removeAccount failed", err);
          }
        }
      } catch (error) {
        console.warn("Token cache clear failed", error);
      }
    }

    // Tüm storage'ları temizle
    sessionStorage.clear();
    localStorage.clear();
    try {
      localStorage.removeItem("activeAuthConfig");
    } catch {}

    // MSAL cache'ini temizle
    try {
      if (inst) {
        await inst.logoutRedirect();
        }
      }
    catch (error) {
      console.warn("MSAL cache clear failed", error);
    }

    msalRef.current = null;
    loginReqRef.current = null;
    setMsalInstance(null);
  };

  const logout = async () => {
    await clearAuthCache();
    if (msalInstance) {
      await msalInstance.logoutRedirect();
    }
    dispatch({ type: Types.Logout });
  };

  const setUserInfo = (userInfo: AccountInfo | null, companyInfo: any) => {
    dispatch({
      type: Types.Initial,
      payload: {
        isAuthenticated: !!userInfo,
        user: userInfo,
        companyInfo: companyInfo ?? state.companyInfo,
        isLoading: state.isLoading,
      },
    });
  };

  const getToken = async () => {
    const inst = msalRef.current;
    const loginReq = loginReqRef.current;
    if (!inst || !loginReq) return null;
    const account = inst.getActiveAccount() ?? inst.getAllAccounts()[0];
    if (!account) return null;
    try {
      const response = await inst.acquireTokenSilent({
        account,
        scopes: loginReq.scopes,
      });
      return response.accessToken;
    } catch (error) {
      console.warn("Token alınamadı, loginRedirect yapılacak", error);
      await inst.loginRedirect(loginReq);
      return null;
    }
  };

  _getToken = getToken;

  const resetPassword = (email: string) => {
    console.log("resetPassword", email);
  };
  const updateProfile = () => {
    console.log("updateProfile");
  };
  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => {
    console.log("register", { email, password, firstName, lastName });
  };

  return (
    <AzureEntraContext.Provider
      value={{
        ...state,
        method: "msal",
        login,
        logout,
        setUserInfo,
        initMsal,
        resetPassword,
        updateProfile,
        register,
        getToken,
        isLoading: state.isLoading,
      }}
    >
      {children}
    </AzureEntraContext.Provider>
  );
}

export { AuthProvider };
